import type { Task } from "@engram/core";
import { REPEAT_RULES, describeRepeat, repeatOf, type Repeat } from "@engram/core/tasks";
import { Bell, Calendar, ChevronDown, FileText, Flag, Pencil, Repeat as RepeatIcon, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { createTask, defaultTaskNoteId, deleteTask, moveTask, toggleTask, updateTask } from "../db/actions";
import { useQuery, useRow } from "../db/client";
import { Popover, useAnchor } from "./Menu";
import { dateFmt } from "./format";

/*
 * Evernote's task dialog, for a new task (sidebar, Tasks page, Alt+T) and for editing one (a row on the Tasks page,
 * a task's details button in a note): the note it lives in, title, due date (Today / Tomorrow / Custom / Repeat),
 * reminder (In 1 hour / In 4 hours / Custom) and flag. Nothing is written until Create task / Save.
 */

type Draft = Pick<Task, "note_id" | "title" | "due_at" | "due_all_day" | "reminder_at" | "flagged" | "recurrence">;

const HOUR = 3600000;
const startOfDay = (ms: number) => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};
const pad = (n: number) => String(n).padStart(2, "0");
const dateInput = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const timeInput = (ms: number) => {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function formatWhen(ms: number, allDay: boolean): string {
  const today = startOfDay(Date.now());
  const day = startOfDay(ms);
  const label =
    day === today ? "Today" : day === today + 86400000 ? "Tomorrow" : day === today - 86400000 ? "Yesterday" :
    dateFmt({ month: "short", day: "numeric", ...(new Date(ms).getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) }).format(ms);
  return allDay ? label : `${label}, ${dateFmt({ hour: "numeric", minute: "2-digit" }).format(ms)}`;
}

export function TaskDialog({ id, noteId, onClose }: { id?: string; noteId?: string; onClose: () => void }) {
  const existing = useRow<Task>(id ? "SELECT * FROM tasks WHERE id = ?" : null, [id], ["tasks"]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [touched, setTouched] = useState(false);
  const [customDue, setCustomDue] = useState(false);
  const [customReminder, setCustomReminder] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const [, navigate] = useLocation();

  // Start from the task, or a blank one in the given note (else the default task note).
  useEffect(() => {
    if (draft) return;
    if (id) {
      if (existing) setDraft({ ...existing });
      return;
    }
    void (noteId ? Promise.resolve(noteId) : defaultTaskNoteId()).then((n) =>
      setDraft({ note_id: n, title: "", due_at: null, due_all_day: 0, reminder_at: null, flagged: 0, recurrence: null }),
    );
  }, [id, existing, noteId, draft]);
  useEffect(() => {
    if (id && existing === null) onClose(); // deleted elsewhere
  }, [id, existing, onClose]);

  const note = useRow<{ id: string; title: string }>(draft ? "SELECT id, title FROM notes WHERE id = ?" : null, [draft?.note_id], ["notes"]);
  if (!draft) return null;
  const set = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const done = existing?.completed_at != null;
  const missingTitle = !draft.title.trim();

  const save = async () => {
    if (missingTitle) {
      setTouched(true);
      titleRef.current?.focus();
      return;
    }
    const fields = { title: draft.title.trim(), due_at: draft.due_at, due_all_day: draft.due_at == null ? 0 : draft.due_all_day, reminder_at: draft.reminder_at, flagged: draft.flagged, recurrence: draft.due_at == null ? null : draft.recurrence };
    if (!id) await createTask({ ...fields, noteId: draft.note_id, flagged: !!draft.flagged });
    else {
      await updateTask(id, fields);
      if (existing && draft.note_id !== existing.note_id) await moveTask(id, draft.note_id);
    }
    onClose();
  };

  const today = startOfDay(Date.now());
  const dueChip = (label: string, at: number) => (
    <button className={`opt-chip${draft.due_at === at && draft.due_all_day ? " on" : ""}`} onClick={() => set({ due_at: at, due_all_day: 1 })}>
      {label}
    </button>
  );

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal task-dialog"
        role="dialog"
        aria-label={id ? "Edit task" : "New task"}
        onKeyDown={(e) => {
          // Only Escape pressed in the dialog itself: its pickers are portaled, and their Escape just closes them.
          if (e.key === "Escape" && e.currentTarget.contains(e.target as Node)) onClose();
          else if (e.key === "Enter" && (e.target as HTMLElement).classList.contains("td-title-input")) {
            e.preventDefault();
            void save();
          }
        }}
      >
        <NotePicker current={draft.note_id} title={note?.title ?? ""} canGo={!!id} onPick={(n) => set({ note_id: n })} onGo={() => (onClose(), navigate(`/note/${draft.note_id}`))} />

        <div className="td-title-row">
          <button
            className={`task-check${done ? " checked" : ""}`}
            disabled={!id}
            onClick={() => id && void toggleTask(id)}
            aria-label={done ? "Mark incomplete" : "Mark complete"}
          />
          <div className="grow">
            <input
              ref={titleRef}
              className={`td-title-input${done ? " done" : ""}`}
              autoFocus={!id}
              placeholder="Enter task"
              value={draft.title}
              onChange={(e) => set({ title: e.target.value })}
              onBlur={() => setTouched(true)}
            />
            {touched && missingTitle && <div className="field-error">This field is required</div>}
          </div>
        </div>

        <div className="td-field">
          <span className="td-label"><Calendar size={15} /> Due date</span>
          <div className="td-options">
            {draft.due_at != null && !(draft.due_all_day && (draft.due_at === today || draft.due_at === today + 86400000)) ? (
              <span className="opt-chip on value">
                {formatWhen(draft.due_at, !!draft.due_all_day)}
                <button aria-label="Clear due date" onClick={() => set({ due_at: null, recurrence: null })}><X size={12} /></button>
              </span>
            ) : (
              <>
                {dueChip("Today", today)}
                {dueChip("Tomorrow", today + 86400000)}
              </>
            )}
            <button className={`opt-chip${customDue ? " on" : ""}`} onClick={() => setCustomDue((v) => !v)}>
              <Pencil size={12} /> Custom
            </button>
            <RepeatChip value={draft.recurrence} onChange={(r) => set({ recurrence: r, ...(draft.due_at == null && r ? { due_at: today, due_all_day: 1 } : {}) })} />
          </div>
          {customDue && (
            <div className="td-custom">
              <input
                type="date"
                aria-label="Due date"
                value={draft.due_at != null ? dateInput(draft.due_at) : ""}
                onChange={(e) => {
                  if (!e.target.value) return set({ due_at: null, recurrence: null });
                  const [y, m, d] = e.target.value.split("-").map(Number) as [number, number, number];
                  const t = draft.due_at != null && !draft.due_all_day ? new Date(draft.due_at) : null;
                  set({ due_at: new Date(y, m - 1, d, t?.getHours() ?? 0, t?.getMinutes() ?? 0).getTime(), due_all_day: t ? 0 : 1 });
                }}
              />
              <input
                type="time"
                aria-label="Due time"
                disabled={draft.due_at == null}
                value={draft.due_at != null && !draft.due_all_day ? timeInput(draft.due_at) : ""}
                onChange={(e) => {
                  if (draft.due_at == null) return;
                  const base = new Date(startOfDay(draft.due_at));
                  if (!e.target.value) return set({ due_at: base.getTime(), due_all_day: 1 });
                  const [hh, mm] = e.target.value.split(":").map(Number) as [number, number];
                  base.setHours(hh, mm);
                  set({ due_at: base.getTime(), due_all_day: 0 });
                }}
              />
            </div>
          )}
        </div>

        <div className="td-field">
          <span className="td-label"><Bell size={15} /> Reminder</span>
          <div className="td-options">
            {draft.reminder_at != null ? (
              <span className="opt-chip on value">
                {formatWhen(draft.reminder_at, false)}
                <button aria-label="Clear reminder" onClick={() => set({ reminder_at: null })}><X size={12} /></button>
              </span>
            ) : (
              <>
                <button className="opt-chip" onClick={() => set({ reminder_at: Math.ceil((Date.now() + HOUR) / 60000) * 60000 })}>In 1 hour</button>
                <button className="opt-chip" onClick={() => set({ reminder_at: Math.ceil((Date.now() + 4 * HOUR) / 60000) * 60000 })}>In 4 hours</button>
              </>
            )}
            <button className={`opt-chip${customReminder ? " on" : ""}`} onClick={() => setCustomReminder((v) => !v)}>
              <Pencil size={12} /> Custom
            </button>
          </div>
          {customReminder && (
            <div className="td-custom">
              <input
                type="datetime-local"
                aria-label="Reminder"
                value={draft.reminder_at != null ? `${dateInput(draft.reminder_at)}T${timeInput(draft.reminder_at)}` : ""}
                onChange={(e) => set({ reminder_at: e.target.value ? new Date(e.target.value).getTime() : null })}
              />
            </div>
          )}
          <div className="td-hint">Reminders notify you on your phone.</div>
        </div>

        <div className="td-field">
          <span className="td-label"><Flag size={15} /> Flag</span>
          <button className={`switch${draft.flagged ? " on" : ""}`} role="switch" aria-checked={!!draft.flagged} aria-label="Flag" onClick={() => set({ flagged: draft.flagged ? 0 : 1 })}>
            <span />
          </button>
        </div>

        <div className="modal-actions">
          {id && (
            <button
              className="btn link danger-text"
              onClick={async () => {
                await deleteTask(id);
                onClose();
              }}
            >
              Delete task
            </button>
          )}
          <div className="grow" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => void save()}>{id ? "Save" : "Create task"}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function RepeatChip({ value, onChange }: { value: string | null; onChange: (r: string | null) => void }) {
  const m = useAnchor();
  const current = repeatOf(value);
  return (
    <>
      <button className={`opt-chip${value ? " on" : ""}`} onClick={m.open}>
        <RepeatIcon size={12} /> {value ? describeRepeat(value) : "Repeat"}
      </button>
      {m.anchor && (
        <Popover anchor={m.anchor} onClose={m.close} className="menu">
          <button className="menu-item" onClick={() => (onChange(null), m.close())}>
            <span className="menu-icon">{!value && "✓"}</span>
            <span className="menu-label">Doesn't repeat</span>
          </button>
          {(Object.keys(REPEAT_RULES) as Repeat[]).map((r) => (
            <button key={r} className="menu-item" onClick={() => (onChange(REPEAT_RULES[r]), m.close())}>
              <span className="menu-icon">{current === r && "✓"}</span>
              <span className="menu-label">{describeRepeat(REPEAT_RULES[r])}</span>
            </button>
          ))}
        </Popover>
      )}
    </>
  );
}

/** The note chip at the top: go to the note, or move the task to a recent note or any note found by title. */
function NotePicker({ current, title, canGo, onPick, onGo }: { current: string; title: string; canGo: boolean; onPick: (id: string) => void; onGo: () => void }) {
  const m = useAnchor();
  const [finding, setFinding] = useState(false);
  const [q, setQ] = useState("");
  const recent = useQuery<{ id: string; title: string }>(
    m.anchor ? "SELECT id, title FROM notes WHERE deleted = 0 AND trashed_at IS NULL AND id <> ? ORDER BY updated DESC LIMIT 5" : null,
    [current],
    ["notes"],
  );
  const found = useQuery<{ id: string; title: string }>(
    m.anchor && finding ? "SELECT id, title FROM notes WHERE deleted = 0 AND trashed_at IS NULL AND title LIKE ? ORDER BY updated DESC LIMIT 30" : null,
    [`%${q.trim()}%`],
    ["notes"],
  );
  const pick = (id: string) => {
    onPick(id);
    m.close();
    setFinding(false);
    setQ("");
  };
  return (
    <>
      <button className="td-note-chip" onClick={m.open}>
        <FileText size={13} />
        <span>{title || "Untitled"}</span>
        <ChevronDown size={13} />
      </button>
      {m.anchor && (
        <Popover anchor={m.anchor} onClose={() => (m.close(), setFinding(false))} className="menu td-note-menu">
          {finding ? (
            <>
              <label className="find-input filter-find">
                <Search size={13} />
                <input autoFocus placeholder="Find a note…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && found?.[0] && pick(found[0].id)} />
              </label>
              <div className="picker-list">
                {(found ?? []).map((n) => (
                  <button key={n.id} className="menu-item" onClick={() => pick(n.id)}>
                    <span className="menu-icon"><FileText size={13} /></span>
                    <span className="menu-label">{n.title || "Untitled"}</span>
                  </button>
                ))}
                {found?.length === 0 && <div className="menu-empty">No notes match</div>}
              </div>
            </>
          ) : (
            <>
              {canGo && (
                <>
                  <button className="menu-item" onClick={() => (m.close(), onGo())}>
                    <span className="menu-icon" />
                    <span className="menu-label">Go to note</span>
                  </button>
                  <div className="menu-sep" />
                </>
              )}
              <div className="menu-heading">Move to</div>
              {(recent ?? []).map((n) => (
                <button key={n.id} className="menu-item" onClick={() => pick(n.id)}>
                  <span className="menu-icon"><FileText size={13} /></span>
                  <span className="menu-label">{n.title || "Untitled"}</span>
                </button>
              ))}
              <button className="menu-item" onClick={() => setFinding(true)}>
                <span className="menu-icon"><Search size={13} /></span>
                <span className="menu-label">Select a note…</span>
              </button>
            </>
          )}
        </Popover>
      )}
    </>
  );
}

/** Opens the task dialog from anywhere: `openTaskDialog()` for a new task, `openTaskDialog(id)` to edit. */
export function openTaskDialog(id?: string, noteId?: string) {
  window.dispatchEvent(new CustomEvent("engram:task-dialog", { detail: { id, noteId } }));
}

/** Mounted once (in the shell); shows the dialog when `openTaskDialog` is called. */
export function TaskDialogHost() {
  const [open, setOpen] = useState<{ id?: string; noteId?: string; key: number } | null>(null);
  useEffect(() => {
    const on = (e: Event) => setOpen({ ...(e as CustomEvent<{ id?: string; noteId?: string }>).detail, key: Date.now() });
    window.addEventListener("engram:task-dialog", on);
    return () => window.removeEventListener("engram:task-dialog", on);
  }, []);
  return open ? <TaskDialog key={open.key} id={open.id} noteId={open.noteId} onClose={() => setOpen(null)} /> : null;
}
